<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { useGradeStore } from '@/stores/gradeStore'
import { useConfigStore } from '@/stores/configStore'
import VersionCard from './VersionCard.vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Pagination } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/pagination'

defineOptions({
  name: 'VersionContent'
})

// Configuration Swiper
const swiperModules = [Pagination]

const gradeStore = useGradeStore()
const configStore = useConfigStore()

// Données des versions depuis le store
const versions = computed(() => gradeStore.gradesList)

// Version active
const activeVersion = computed(() => configStore.currentGrade?.item_id || null)

// Fonction pour sélectionner une version
function selectVersion(versionId: string) {
  console.log(`Version sélectionnée: ${versionId}`)
  configStore.selectGrade(versionId)
}

// Sélection automatique de la première version au démarrage
onMounted(async () => {
  // Charger les grades si pas encore chargés
  if (versions.value.length === 0) {
    try {
      await gradeStore.fetchGrades()
    } catch (error) {
      console.error('Erreur lors du chargement des grades:', error)
    }
  }
})

//Watcher pour sélectionner automatiquement la première version
watch(versions, (newVersions) => {
  if (newVersions.length > 0 && !activeVersion.value) {
    const firstVersion = newVersions[0]
    console.log(`Auto-sélection de la première version: ${firstVersion.item_id}`)
    selectVersion(firstVersion.item_id)
  }
}, { immediate: true })

// Émettre un événement pour naviguer vers l'étape suivante
const emit = defineEmits(['nextStep'])

function handleConfigure() {
  emit('nextStep')
}
</script>

<template>
  <div class="version-content">
    <p>Choisissez votre version</p>
    <Swiper
      :modules="swiperModules"
      :slides-per-view="'auto'"
      :space-between="16"
      :watch-overflow="true"
      :center-insufficient-slides="true"
      :observer="true"
      :observe-parents="true"
      :navigation="false"
      :pagination="{ clickable: true }"
      class="versions-swiper"
    >
      <SwiperSlide
        v-for="version in versions"
        :key="version.item_id"
        class="version-slide"
      >
        <VersionCard
          :grade-id="version.item_id"
          :full-name="version.fullName"
          :motorization="version.motorization"
          :battery="version.battery"
          :base-price="version.basePrice"
          :thumbnail="version.thumbnail"
          :is-active="activeVersion === version.item_id"
          @select="selectVersion(version.item_id)"
        />
      </SwiperSlide>
    </Swiper>
    <div class="configure-section">
      <button
        class="precommand-link"
        @click="handleConfigure"
        :disabled="!activeVersion"
      >
        Configurer
      </button>
    </div>
  </div>
</template>

<style scoped>
/* Conteneur principal - empêche le débordement */
.version-content {
  overflow: hidden;
  max-width: 100%;
}

/* Swiper carousel */
.versions-swiper {
  width: 100%;
  min-width: 0; /* Empêche le flex item de dépasser */
  overflow: hidden;
  padding-bottom: 2.5rem; /* Espace pour la pagination */
}

/* Slide avec largeur automatique basée sur le contenu */
.version-slide {
  width: auto;
  flex-shrink: 0;
}

/* Style de la pagination */
.versions-swiper :deep(.swiper-pagination) {
  bottom: 0;
}

.versions-swiper :deep(.swiper-pagination-bullet) {
  background: var(--color-text-primary);
  opacity: 0.3;
}

.versions-swiper :deep(.swiper-pagination-bullet-active) {
  opacity: 1;
}

/* Masquer la pagination quand swiper est verrouillé (watchOverflow) */
.versions-swiper.swiper-locked :deep(.swiper-pagination) {
  display: none;
}

/* Section du bouton Configurer */
.configure-section {
  padding: 1rem 0 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

/* Style du bouton Configurer (même que precommand-link) */
.precommand-link {
  padding: 0.5em 3em;
  background-color: var(--color-text-primary);
  color: var(--color-primary);
  border: none;
  border-radius: 0.25rem;
  font-size: 1rem;
  font-weight: 300;
  cursor: pointer;
  transition: background-color 0.3s ease, color 0.3s ease;
  letter-spacing: 0.5px;
  text-decoration: none;
}

.precommand-link:hover:not(:disabled) {
  background-color: var(--color-hover);
  color: var(--color-primary);
}

.precommand-link:active:not(:disabled) {
  color: var(--color-primary);
}

.precommand-link:disabled {
  background-color: var(--color-disabled, #999);
  color: var(--color-disabled-text, #666);
  cursor: not-allowed;
  pointer-events: none;
}

/* Responsive */
@media (max-width: 768px) {
  .versions-swiper {
    padding-bottom: 2rem;
  }

  .configure-section {
    padding: 20px 16px 0;
  }

  .precommand-link {
    padding: 16px 24px;
  }
}

@media (max-width: 480px) {
  .configure-section {
    padding: 16px 12px 0;
  }

  .precommand-link {
    padding: 14px 20px;
  }
}
</style>